import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  ChevronDown,
  LayoutDashboard,
  Calculator,
  Zap,
} from "lucide-react";
import logo from "../Images/logo.png";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProductsOpen(false);
  }, [location]);

  return (
    <>
      <nav
        className={`fixed top-0  left-0 right-0 z-[100] transition-all duration-300 ${
          scrolled ? "bg-white shadow-lg h-16" : "bg-slate-50/90 h-20"
        }`}
      >
        <div className="max-w-7xl mx-auto  px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex  justify-between items-center h-full">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                className="h-10 w-auto md:h-12 transition-all"
                src={logo}
                alt="Bumppy Logo"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <Link
                to="/"
                className="text-slate-700 hover:text-blue-600 transition-colors font-medium"
              >
                Home
              </Link>

              <div className="relative group">
                <button
                  onMouseEnter={() => setIsProductsOpen(true)}
                  onMouseLeave={() => setIsProductsOpen(false)}
                  className="text-slate-700 hover:text-blue-600 transition-colors font-medium flex items-center py-4"
                >
                  Products{" "}
                  <ChevronDown
                    className={`ml-1 w-4 h-4 transition-transform ${isProductsOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isProductsOpen && (
                  <div
                    onMouseEnter={() => setIsProductsOpen(true)}
                    onMouseLeave={() => setIsProductsOpen(false)}
                    className="absolute top-full -left-4 w-72 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 animate-fadeIn"
                  >
                    <div className="px-4 py-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        Bumppy Services
                      </p>
                      <Link
                        to="/product/bill-payment"
                        className="flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
                      >
                        <Zap size={18} className="text-blue-500" /> Bill Payment
                      </Link>
                    </div>
                    <div className="border-t border-slate-50 my-2"></div>
                    <div className="px-4 py-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        Business Tools
                      </p>
                      <Link
                        to="/product/income-calculator"
                        className="flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
                      >
                        <Calculator size={18} className="text-emerald-500" />{" "}
                        Income Calculator
                      </Link>
                      <Link
                        to="/product/analytics"
                        className="flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
                      >
                        <LayoutDashboard
                          size={18}
                          className="text-purple-500"
                        />{" "}
                        Analytics Dashboard
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/retailer"
                className="text-slate-700 hover:text-blue-600 transition-colors font-medium"
              >
                Services
              </Link>
              <Link
                to="/about"
                className="text-slate-700 hover:text-blue-600 transition-colors font-medium"
              >
                Company
              </Link>
              <Link
                to="/pricing"
                className="text-slate-700 hover:text-blue-600 transition-colors font-medium"
              >
                Pricing
              </Link>

              <div className="h-6 w-[1px] bg-slate-200 mx-2"></div>

              <a
                href="https://dashboard.bumppy.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-700 hover:text-blue-600 font-semibold"
              >
                Login
              </a>
              <a
                href="https://dashboard.bumppy.com/register"
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-full font-bold hover:bg-slate-900 shadow-lg shadow-indigo-200 transition-all active:scale-95"
              >
                Sign Up
              </a>
            </div>

            <div className="md:hidden flex items-center gap-4">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-slate-700"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        <div
          className={`fixed inset-0 z-[-1] bg-slate-900/50 backdrop-blur-lg transition-opacity duration-300 md:hidden ${
            isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div
          className={`fixed top-0 right-0 bottom-0 w-[100%] max-w-sm bg-white z-[101] shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="p-6 border-b flex justify-between items-center">
              <img className="h-8 w-auto" src={logo} alt="Logo" />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 bg-slate-100 rounded-full text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              <Link
                to="/"
                className="block p-4 text-lg font-bold text-slate-800 bg-slate-50 rounded-2xl"
              >
                Home
              </Link>

              <div className="bg-slate-50 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="flex items-center justify-between w-full p-4 text-lg font-bold text-slate-800"
                >
                  Products{" "}
                  <ChevronDown
                    className={`transition-transform duration-300 ${isProductsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`transition-all duration-300 ${isProductsOpen ? "max-h-56 opacity-100 pb-4" : "max-h-0 opacity-0 overflow-hidden"}`}
                >
                  <Link
                    to="/product/bill-payment"
                    className="flex items-center gap-3 px-8 py-3 text-slate-600 font-medium"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>{" "}
                    Bill Payment
                  </Link>
                  <Link
                    to="/product/income-calculator"
                    className="flex items-center gap-3 px-8 py-3 text-slate-600 font-medium"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>{" "}
                    Income Calculator
                  </Link>
                  <Link
                    to="/product/analytics"
                    className="flex items-center gap-3 px-8 py-3 text-slate-600 font-medium"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>{" "}
                    Analytics Dashboard
                  </Link>
                </div>
              </div>

              <Link
                to="/retailer"
                className="block p-4 text-lg font-bold text-slate-800 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                Retailers
              </Link>
              <Link
                to="/about"
                className="block p-4 text-lg font-bold text-slate-800 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                Company
              </Link>
              <Link
                to="/pricing"
                className="block p-4 text-lg font-bold text-slate-800 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                Pricing
              </Link>
            </div>

            <div className="p-6 border-t bg-slate-50 grid grid-cols-2 gap-4">
              <a
                href="https://dashboard.bumppy.com/"
                className="flex items-center justify-center p-4 bg-white border border-slate-200 rounded-2xl font-bold text-slate-700"
              >
                Login
              </a>
              <a
                href="https://dashboard.bumppy.com/register"
                className="flex items-center justify-center p-4 bg-blue-600 rounded-2xl font-bold text-white shadow-lg shadow-blue-100"
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </>
  );
};

export default Navbar;
